import { useAccountCrumbs } from '@thd-olt-component-react/customer-account-navigation';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Metadata } from '@thd-olt-component-react/metadata';
import React, { useEffect, useRef, useState } from 'react';
import {
  EditDeliveryInstructionsDrawer,
  IEditDeliveryInstructionsDrawer,
} from '../components/order-details/EditDeliveryInstructions/EditDeliveryInstructionsDrawer';
import {
  NextPreviousButtons,
  OrderDetailsHeader,
  OrderPaymentInformation,
  OrderTotal,
  SkeletonHeader,
} from '../components/order-details/index';
import { OrderDetailsPageHeader } from '../components/order-details/order-details-page-header/index';
import { OrderDetailsMain } from '../components/order-details/OrderDetailsMain';
import { AlertBanner } from '../components/shared/alert-banner/AlertBanner';
import {
  CancellationDrawer,
  ICancellationDrawer,
} from '../components/shared/cancellation-drawer/CancellationDrawer';
import PageLayout from '../components/shared/PageLayout';
import { PHXSatisfactionSurvey } from '../components/shared/PHXSatisfactionSurvey';
import { CommonProvider, Phase0BarcodeProvider } from '../contexts';
import { OrderDetailsProvider } from '../contexts/OrderDetailsProvider';
import { EditDeliveryInstructionsProvider } from '../contexts/EditDeliveryInstructionsContext';
import { OrderProvider } from '../contexts/OrderContext';
import {
  useCommonContext,
  useEditDeliveryInstructionsContext,
  useOrderDetailsContext,
} from '../hooks/useContexts';
import { ICancelInfo, ICancellationItem } from '../models/cancel-items';
import { IOPExpSessionData } from '../models/common';
import { IOrderDetailsResponse } from '../models/order-details';
import { getWindow, PHODPageData } from '../utils/general';
import { redirectToSignIn } from '../utils/http-helper';
import {
  setPurchaseDetailsPageError,
  trackOrderDetailsPage,
} from '../utils/order-details/analytics';
import {
  moveCanceledObjectToLastIndex,
  processFulfillmentGroup,
} from '../utils/order-details/OrderDetailsUtils';
import { PTCProvider } from '../contexts/PTCContext';
import { ViewChargedDetails } from '../components/order-details/ViewChargedDetails/ViewChargedDetails';

interface OrderDetailsProps {
  customerType: 'b2b' | 'b2c';
  customerRole: string;
}

const OrderDetailsContent = ({
  customerType,
  customerRole,
}: OrderDetailsProps) => {
  // Contexts
  const { isMobile } = useCommonContext();
  const {
    orderDetailsData,
    orderDetailsError,
    orderDetailsLoaded,
    orderDetailsRequestParams,
    alertState,
    setAlertState,
    exportAlertState,
    setExportAlertState,
    nickName,
    orderNumber,
  } = useOrderDetailsContext();
  const { setEditDeliveryInstructionsDrawerOpen } =
    useEditDeliveryInstructionsContext();
  const crumbs = useAccountCrumbs([
    {
      label: 'Purchase History',
      path: '/myaccount/purchase-history',
    },
    {
      label: 'Order Details',
      path: '/myaccount/order-details',
    },
  ]);

  // States
  const [cancellationItems, setCancellationItems] = useState<
    ICancellationItem[]
  >([]);
  const [cancellationDrawerOpen, setCancellationDrawerOpen] =
    useState<boolean>(false);
  const [cancelInfo, setCancelInfo] = useState<ICancelInfo>({} as ICancelInfo);

  // Check for applying order detail api error analytics just for order details page
  if (orderDetailsError) {
    setPurchaseDetailsPageError(orderDetailsError);
  }
  let OPExpSessionData = PHODPageData();
  getWindow()?.sessionStorage.setItem(
    'purchaseHistoryPage',
    JSON.stringify({
      ...(OPExpSessionData as IOPExpSessionData),
      ODLastVisited: new Date(),
    }),
  );
  OPExpSessionData = PHODPageData();

  const LineItemsProps = {
    orderDetailsData,
  };

  const encryptedBigReceiptRequestIndex =
    orderDetailsData?.encryptedBigReceiptRequests
      ? Object.keys(orderDetailsData?.encryptedBigReceiptRequests)?.find(
          (key: string) => key.charAt(0) === 'H',
        )
      : '';
  const encryptedBigReceiptRequest =
    orderDetailsData?.encryptedBigReceiptRequests?.[
      (encryptedBigReceiptRequestIndex ?? 0) as number
    ];

  const encryptedReceiptRequest = orderDetailsData?.encryptedReceiptRequest;

  const missingInfo =
    !(orderDetailsData?.salesDate ?? orderDetailsData?.createDateTime) ||
    !orderDetailsData?.tenders?.length ||
    !orderDetailsData?.orderOrigin ||
    !orderDetailsData?.subTotalAmount === null ||
    !orderDetailsData?.subTotalAmount === undefined ||
    !orderDetailsData?.taxTotalAmount === null ||
    !orderDetailsData?.taxTotalAmount === undefined ||
    !orderDetailsData?.grandTotalAmount === null ||
    !orderDetailsData?.grandTotalAmount === undefined;

  useEffect(() => {
    if (orderDetailsError) {
      // Check to see if response is 401 or 431 to redirect to sign in
      if (
        orderDetailsError.response?.status === 401 ||
        orderDetailsError.response?.status === 431
      ) {
        redirectToSignIn();
      }

      setAlertState({
        alertMessage:
          'We were unable to retrieve your order details. Refresh the page and try again.',
        status: 'error',
      });
      return;
    }

    if (
      !orderDetailsError &&
      orderDetailsData &&
      missingInfo &&
      orderDetailsData.statusDescription !== 'Canceled'
    ) {
      setAlertState({
        alertMessage:
          'Some of your order information is not available at this time. Check back later for full details.',
        status: 'warning',
      });
    }
  }, [orderDetailsData, orderDetailsError]);

  const hasTrackedOrderDetailsPage = useRef(false);

  useEffect(() => {
    if (
      orderDetailsData &&
      !orderDetailsError &&
      !hasTrackedOrderDetailsPage.current
    ) {
      trackOrderDetailsPage(orderDetailsData);
      hasTrackedOrderDetailsPage.current = true;
    }
  }, [orderDetailsData, orderDetailsError]);

  const toggleDrawer = () => {
    setCancellationDrawerOpen(false);
    setEditDeliveryInstructionsDrawerOpen(false);
  };

  const CancellationDrawerProps: ICancellationDrawer = {
    open: cancellationDrawerOpen,
    onClose: toggleDrawer,
    isCancelItemsPage: false,
    cancelAllItems: true,
    orderDetailsData: orderDetailsData as IOrderDetailsResponse,
    orderDetailsRequestParams,
    cancelInfo,
    drawerLineItems: cancellationItems,
    isPostRemorse: cancelInfo.isPostRemorseCancel, // Here goes the flag for PostRemorse button in the Drawer
    STHorBOSSAlert: cancelInfo.isSTHorBOSSRemorseCancel,
  };

  const EditDeliveryInstructionsDrawerProps: IEditDeliveryInstructionsDrawer = {
    toggleDrawer,
  };

  // Destructuring the fulfillment Group if there is any canceled order, into a new fulfillmentType: Cancel
  const destructureFulfillmentGroupForCanceledOrder =
    orderDetailsData?.fulfillmentGroups &&
    processFulfillmentGroup(orderDetailsData?.fulfillmentGroups);
  // Indexing Cancellation Items Object to last (n) place
  const modifiedFulfillmentGroups =
    destructureFulfillmentGroupForCanceledOrder &&
    moveCanceledObjectToLastIndex(destructureFulfillmentGroupForCanceledOrder);

  const displayOrderNumber = () => {
    if (
      orderDetailsData?.registerNumber === 0 &&
      orderDetailsData?.orderNumber?.[0] === 'H'
    ) {
      return (
        orderDetailsData?.orderNumbers?.[1] ?? orderNumber ?? 'UNAVAILABLE'
      );
    }
    if (orderDetailsData?.orderNumber || orderDetailsData?.orderNumbers?.[0]) {
      return (
        orderDetailsData?.orderNumber ?? orderDetailsData?.orderNumbers?.[0]
      );
    }
    return orderNumber ?? 'UNAVAILABLE';
  };

  return (
    <PageLayout
      crumbs={crumbs}
      customerRole={customerRole}
      customerType={customerType}
      internalRoutes={['/myaccount/order-details']}
      headerComponent={<OrderDetailsPageHeader customerType={customerType} />}
      isMobile={isMobile}
      pageTitle="Order Details"
    >
      <Phase0BarcodeProvider
        orderDetailsData={orderDetailsData}
        orderDetailsLoaded={orderDetailsLoaded}
      >
        <Metadata title="The Home Depot" />
        <div className="sui-w-full sui-max-w-[1039px]">
          <PHXSatisfactionSurvey />
        </div>
        {orderDetailsLoaded && orderDetailsData ? (
          <PTCProvider>
            <div className="sui-grid sui-grid-cols-1 md:sui-grid-cols-2 lg:gap-6">
              <ErrorBoundary name="order-details-header">
                <OrderDetailsHeader
                  orderDetailsData={orderDetailsData}
                  encryptedBigReceiptRequest={encryptedBigReceiptRequest}
                  encryptedReceiptRequest={encryptedReceiptRequest}
                  orderNumber={displayOrderNumber()}
                  customerType={customerType}
                  filteredFulfillmentObject={modifiedFulfillmentGroups}
                />
              </ErrorBoundary>
              {missingInfo && alertState && (
                <div className="sui-col-span-1 md:sui-col-span-2 sui-order-1">
                  <AlertBanner
                    headline={alertState?.alertTitle || ''}
                    status={alertState?.status || 'info'}
                    onClose={() => setAlertState(null)}
                  >
                    {alertState?.alertMessage}
                  </AlertBanner>
                </div>
              )}
              {exportAlertState && (
                <div className="sui-col-span-1 md:sui-col-span-2 sui-order-1">
                  <AlertBanner
                    headline={exportAlertState?.alertTitle || ''}
                    status={exportAlertState?.status || 'info'}
                    onClose={() => setExportAlertState(null)}
                  >
                    {exportAlertState?.alertMessage}
                  </AlertBanner>
                </div>
              )}
              <ErrorBoundary name="order-payment-information">
                <OrderPaymentInformation
                  orderDetailsData={orderDetailsData}
                  customerType={customerType}
                  nickName={nickName}
                />
              </ErrorBoundary>
              {/* This section holds the fulfillment details, line items, tracking. It is the main section of the order details page */}
              <OrderDetailsMain
                fulfillmentGroups={modifiedFulfillmentGroups}
                LineItemsProps={LineItemsProps}
                orderDetailsData={orderDetailsData}
                orderDetailsRequestParams={orderDetailsRequestParams}
                orderNumber={
                  orderDetailsData?.orderNumber ??
                  orderDetailsData?.orderNumbers?.[0]
                }
                setCancelInfo={setCancelInfo}
                setCancellationDrawerOpen={setCancellationDrawerOpen}
                setCancellationItems={setCancellationItems}
                customerType={customerType}
              />
              {/* This section holds the order total and view charged details components */}
              <div
                className={`sui-col-span-1 sui-order-3 md:sui-col-span-1  md:sui-order-2 ${
                  !isMobile ? 'sui-ml-12' : ''
                }`}
              >
                <OrderTotal
                  shippingCharge={orderDetailsData?.shippingCharge}
                  deliveryCharge={orderDetailsData?.deliveryCharge}
                  hasPreferredDiscFlag={orderDetailsData?.preferredDiscFlg}
                  hasProXtraSaving={Boolean(
                    orderDetailsData?.otherDiscountAmount,
                  )}
                  orderDetailsData={orderDetailsData}
                  customerType={customerType}
                  filteredFulfillmentObject={modifiedFulfillmentGroups}
                  militaryDiscount={orderDetailsData?.militaryDiscount ?? {}}
                />
                <div className="sui-pt-4">
                  <ViewChargedDetails />
                </div>
              </div>
              {/* This section holds the next and previous buttons */}
              <ErrorBoundary name="next-previous-buttons">
                <NextPreviousButtons />
              </ErrorBoundary>
              {/* This section holds the drawers and other components that hidden until actioned */}
              <ErrorBoundary name="cancellation-drawer">
                <CancellationDrawer {...CancellationDrawerProps} />
              </ErrorBoundary>
              <ErrorBoundary name="edit-delivery-instructions-drawer">
                <EditDeliveryInstructionsDrawer
                  {...EditDeliveryInstructionsDrawerProps}
                />
              </ErrorBoundary>
            </div>
          </PTCProvider>
        ) : (
          <div className="sui-grid sui-grid-cols-1 md:sui-grid-cols-2 lg:gap-6 sui-max-w-[1039px]">
            {alertState && (
              <div className="sui-col-span-1 md:sui-col-span-2 sui-order-1">
                <AlertBanner status={alertState?.status || 'info'}>
                  {alertState?.alertMessage}
                </AlertBanner>
              </div>
            )}
            <SkeletonHeader isMobile={isMobile} />
            <ErrorBoundary name="next-previous-buttons">
              <NextPreviousButtons />
            </ErrorBoundary>
          </div>
        )}
      </Phase0BarcodeProvider>
    </PageLayout>
  );
};

export const OrderDetails = ({
  customerType,
  customerRole,
}: OrderDetailsProps) => (
  <CommonProvider>
    <OrderProvider>
      <OrderDetailsProvider
        customerType={customerType}
        customerRole={customerRole}
      >
        <EditDeliveryInstructionsProvider>
          <OrderDetailsContent
            customerType={customerType}
            customerRole={customerRole}
          />
        </EditDeliveryInstructionsProvider>
      </OrderDetailsProvider>
    </OrderProvider>
  </CommonProvider>
);

OrderDetails.displayName = 'Order Details';
